// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useMemo } from 'react';

import { useInternalEditor, EditorCollector, useInternalEditorReturnType } from '../editor/useInternalEditor';
import { EditorStore } from '../editor';
import { Delete, Overwrite, OverwriteFnReturnType } from '../utils/utilityTypes';
import { Account, Category, DesignSettings } from '../interfaces';

type PrivateActions = 'addLinkedNodeFromTree' | 'setNodeEvent' | 'setDOM' | 'replaceNodes' | 'reset';

const getPublicActions = (actions: EditorStore['actions']) => {
    // dont expose private actions
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { addLinkedNodeFromTree, setDOM, setNodeEvent, replaceNodes, reset, ...EditorActions } = actions;

    return EditorActions;
};

export type WithoutPrivateActions<S = null> = Delete<
    useInternalEditorReturnType<S>['actions'],
    PrivateActions | 'history'
> & {
    history: Overwrite<
        useInternalEditorReturnType<S>['actions']['history'],
        {
            ignore: OverwriteFnReturnType<
                useInternalEditorReturnType<S>['actions']['history']['ignore'],
                PrivateActions
            >;
            throttle: OverwriteFnReturnType<
                useInternalEditorReturnType<S>['actions']['history']['throttle'],
                PrivateActions
            >;
        }
    >;
};

export type useEditorReturnType<S = null> = Overwrite<
    useInternalEditorReturnType<S>,
    {
        actions: WithoutPrivateActions;
        query: Delete<useInternalEditorReturnType<S>['query'], 'deserialize'>;
        designSettings: DesignSettings;
        account: Account;
    }
>;

/**
 * A Hook that that provides methods and information related to the entire editor state.
 */
export function useEditor(): useEditorReturnType;
export function useEditor<S>(collect: EditorCollector<S>): useEditorReturnType<S>;

export function useEditor<S>(collect?: unknown): useEditorReturnType<S> {
    const {
        connectors,
        actions: internalActions,
        // dont expose private query
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        query: { deserialize, ...query },
        store,
        ...collected
    } = useInternalEditor(collect);

    const EditorActions = getPublicActions(internalActions);

    const actions = useMemo(() => {
        return {
            ...EditorActions,
            history: {
                ...EditorActions.history,
                ignore: (...args) => getPublicActions(EditorActions.history.ignore(...args)),
                throttle: (...args) => getPublicActions(EditorActions.history.throttle(...args)),
            },
        };
    }, [EditorActions]);

    return {
        connectors,
        actions,
        query,
        designSettings: query.getDesignSettings(),
        account: query.getAccountInfo(),
        store,
        ...(collected as unknown),
    };
}
